import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import React, { ReactElement } from 'react'
import { startCase } from 'lodash'
import Link from 'next/link'
import styles from './StudyGuideBannerLink.module.scss'
import { Outline } from '@/components/static/images/owlBranding/outline'
import { Cards } from '@/components/static/images/owlBranding/cards'
import { Calculator } from '@/components/static/images/owlBranding/calculator'
import { World } from '@/components/static/images/owlBranding/randoWorld'
import { LaptopDashboard } from '@/components/static/images/owlBranding/LaptopDashboard'
import { Glasses } from '@/components/static/images/owlBranding/glasses'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import { CourseConfigType } from 'common/src/courseConfig'

type StudyGuidePage =
    | 'PHILOSOPHY'
    | 'OUTLINES'
    | 'NOTECARDS'
    | 'PRACTICE QUESTIONS'
    | 'OTHER'
    | 'GENERAL TIPS'
interface StudyGuideBannerLinkProps {
    title: StudyGuidePage
    disabled: boolean
    courseConfigType: CourseConfigType
}

const STUDY_GUIDE_PAGE_TO_UI_CONSTANT: Record<
    StudyGuidePage,
    UiLayoutConstant
> = {
    PHILOSOPHY: UiLayoutConstant.STUDY_MATERIALS_GUIDE_PHILOSOPHY,
    OUTLINES: UiLayoutConstant.STUDY_MATERIALS_GUIDE_OUTLINES,
    NOTECARDS: UiLayoutConstant.STUDY_MATERIALS_GUIDE_NOTECARDS,
    'PRACTICE QUESTIONS': UiLayoutConstant.STUDY_MATERIALS_GUIDE_PROBLEMS,
    OTHER: UiLayoutConstant.STUDY_MATERIALS_GUIDE_OTHER,
    'GENERAL TIPS': UiLayoutConstant.STUDY_MATERIALS_GUIDE_TIPS,
}

const STUDY_GUIDE_PAGE_TO_DESCRIPTION_FSA_HEALTH: Record<
    StudyGuidePage,
    string
> = {
    PHILOSOPHY: 'Why Just Enough Prep exists and what it offers',
    OUTLINES: 'Supplement your learnings with more detailed information',
    NOTECARDS: 'Strategies to learn and retain the facts of the exam',
    'PRACTICE QUESTIONS': 'Master math and synthesis questions',
    OTHER: 'Eupheus your AI study partner, instant search, study timer, study buddy, and instructor access',
    'GENERAL TIPS': 'General tips for studying and taking exams',
}

const getStudyPageDescription = (
    page: StudyGuidePage,
    courseConfigType: CourseConfigType
): string => {
    switch (courseConfigType) {
        case CourseConfigType.fsaHealth:
            return STUDY_GUIDE_PAGE_TO_DESCRIPTION_FSA_HEALTH[page]
        default:
            return STUDY_GUIDE_PAGE_TO_DESCRIPTION_FSA_HEALTH[page]
    }
}

const STUDY_PAGE_GUIDE_TO_IMAGE: Record<StudyGuidePage, ReactElement> = {
    PHILOSOPHY: <World />,
    OUTLINES: <Outline />,
    NOTECARDS: <Cards />,
    'PRACTICE QUESTIONS': <Calculator />,
    OTHER: <Glasses />,
    'GENERAL TIPS': <LaptopDashboard />,
}

export const StudyGuideBannerLink: React.FC<StudyGuideBannerLinkProps> = (
    props
): ReactElement => {
    const baseComponent = (
        <div
            className={`${styles.studyGuideBannerLink} ${
                props.disabled ? styles.disabled : ''
            }`}
        >
            <div className={reusableCssClass.centerChildrenVertically}>
                {STUDY_PAGE_GUIDE_TO_IMAGE[props.title]}
            </div>
            <div
                className={`${styles.textContainer} ${reusableCssClass.centerChildrenVertically}`}
            >
                <div className={styles.title}>{startCase(props.title)}</div>
                <div className={styles.description}>
                    {getStudyPageDescription(
                        props.title,
                        props.courseConfigType
                    )}
                </div>
            </div>
        </div>
    )
    if (props.disabled) {
        return baseComponent
    }
    return (
        <Link
            key={props.title}
            href={STUDY_GUIDE_PAGE_TO_UI_CONSTANT[props.title]}
        >
            {baseComponent}
        </Link>
    )
}
